import { Button, Dialog, DialogActions, DialogContent, TextField } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogHeader } from '@omnea-app/omui/DialogHeader'
import { ApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { useDeletePauseReason } from './useDeletePauseReason'
import { PauseReasonsQueryKey } from './useGetPauseReasons'
import { useInsertPauseReason } from './useInsertPauseReason'
import { useUpdatePauseReason } from './useUpdatePauseReason'

/** A null `reasonId` means a reason that does not exist yet, with `text` seeding the input. */
export type PauseReasonDialogTarget = { reasonId: string | null; text: string }

const MAX_REASON_LENGTH = 255

type Props = {
  target: PauseReasonDialogTarget
  onClose: () => void
  /** Called only for a newly added reason, never for a rename. */
  onAdded: (reasonId: string) => void
  onRemoved: (reasonId: string) => void
}

const isApiErrorOfType = (caught: unknown, type: string) => caught instanceof ApiError && caught.type === type

export const PauseReasonDialog = ({ target, onClose, onAdded, onRemoved }: Props) => {
  const { t } = useTranslation('requests', { keyPrefix: 'pause.reason-dialog' })
  const queryClient = useQueryClient()
  const [text, setText] = useState(target.text)
  const [error, setError] = useState<string | null>(null)
  const insertPauseReason = useInsertPauseReason()
  const updatePauseReason = useUpdatePauseReason()
  const deletePauseReason = useDeletePauseReason()

  const { reasonId } = target
  const isSaving = insertPauseReason.isPending || updatePauseReason.isPending
  const isPending = isSaving || deletePauseReason.isPending

  /** Validates, runs the save, and closes — so add and rename differ only in the call they make. */
  const save = async (perform: (name: string) => Promise<void>) => {
    const name = text.trim()
    if (!name) {
      setError(t('errors.empty'))
      return
    }

    try {
      await perform(name)
      onClose()
    } catch (caught) {
      if (isApiErrorOfType(caught, 'ConflictError')) return setError(t('errors.duplicate'))
      if (isApiErrorOfType(caught, 'NotFoundError')) {
        // Someone else removed it, so the list this was chosen from is stale. Refreshing drops the
        // reason from the picker, which is what clears a selection still pointing at it.
        await queryClient.invalidateQueries({ queryKey: PauseReasonsQueryKey })
        // Retrying cannot help once the reason is gone, so it must not read as a transient failure.
        return setError(t('errors.gone'))
      }
      setError(t('errors.save'))
    }
  }

  const addReason = () =>
    save(async (name) => {
      const added = await insertPauseReason.mutateAsync({ reason: name })
      onAdded(added.reason.id)
    })

  const renameReason = (id: string) => () =>
    save(async (name) => {
      await updatePauseReason.mutateAsync({ reasonId: id, text: name })
    })

  const removeReason = (id: string) => async () => {
    try {
      await deletePauseReason.mutateAsync({ reasonId: id })
    } catch (caught) {
      // Already removed is the outcome that was asked for, so report it as done.
      if (!isApiErrorOfType(caught, 'NotFoundError')) {
        setError(t('errors.remove'))
        return
      }
      // The mutation threw, so its own `onSuccess` never refreshed the list — without this the
      // picker keeps offering a reason another admin has already removed.
      await queryClient.invalidateQueries({ queryKey: PauseReasonsQueryKey })
    }
    onRemoved(id)
    onClose()
  }

  const handleClose = () => {
    if (isPending) return
    onClose()
  }

  return (
    <Dialog open onClose={handleClose}>
      <DialogHeader title={reasonId ? t('edit-title') : t('add-title')} onClose={handleClose} disabled={isPending} />
      <DialogContent>
        <TextField
          label={t('label')}
          required
          value={text}
          onChange={(event) => {
            setText(event.target.value)
            setError(null)
          }}
          error={error !== null}
          helperText={error ?? undefined}
          disabled={isPending}
          inputProps={{ maxLength: MAX_REASON_LENGTH }}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        {reasonId ? (
          <>
            <Button
              color="error"
              onClick={() => void removeReason(reasonId)()}
              loading={deletePauseReason.isPending}
              disabled={isPending}
            >
              {t('remove')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => void renameReason(reasonId)()}
              loading={isSaving}
              disabled={isPending}
            >
              {t('save')}
            </Button>
          </>
        ) : (
          <>
            <Button color="secondary" variant="contained" onClick={handleClose} disabled={isPending}>
              {t('cancel', { ns: 'common', keyPrefix: '' })}
            </Button>
            <Button variant="contained" color="primary" onClick={() => void addReason()} loading={isSaving} disabled={isPending}>
              {t('add')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
